import React, {useLayoutEffect, useMemo, useRef, useState} from 'react'

import VScroll from "../../../../core/gd-components/scrolling/custom-v-scroll/v-scroll"
import FormManager from "../../../../core/gd-form/common/form-manager"
import AIChatMessage from "../../../../core/model2/ai/chat-message/chat-message"
import AIChatMessage_Collection from "../../../../core/model2/ai/chat-message/collection"
import AIChat from "../../../../core/model2/ai/chat/chat"
import AiChatMessage_TokenUsage from "../../../../core/model2/ai/chat-message/token-usage/token-usage"
import {AIChatContextType, AiChatType} from "../../../model2/ai/@const"
import AiChatModule from "../ai-chat"
import sendAIChatMessage from "../actions/send-message"
import createNewAIChat from '../actions/new-ai-chat'

import WriteWithAi_InputMessage from "./input-message/input-message"
import WriteWithAi_ActionButtons from "./action-buttons/buttons"
import {LexicalEditor} from "lexical"
import WriteWithAiChat_Messages from "./message-history/messages"
import {EVENT_BUS_LAYOUT_ON_HEIGHT_MAIN_CHANGE} from "../../../../layout/layout/bus-events"
import {useForceUpdate} from "../../../gd/utils/dom-and-react/react-utils"
import AIChat_SuggestedActions from '../../../../app/ai-chat/components/chat/suggested_actions/suggested_actions'
import aiChat_initChatMessageForm from "../../../../app/ai-chat/components/chat/actions/init-chat-message-form"
import AIChatContext from "../../../model2/ai/chat/context/context"
import {EditorAIOptions} from "../../../gd-components/editor/editor"
import ScreenUpgradeSimple from "../../../gd-components/screen/upgrade/upgrade-simple";
import {featureAccess} from "../../../gd/access-control/feature-access/feature-access";
import {Feature} from "../../../gd/access-control/feature-access/const";

type Props = {
    className?      : string

    editor          : LexicalEditor
    aiOptions?      : EditorAIOptions

    selectedText?   : string
}

// const debug = "WriteWithAiChat"
const debug = false

export function WriteWithAiChat(props: Props) {

    // license acceess
    if (!featureAccess(Feature.AI)) {
        return <ScreenUpgradeSimple feature={Feature.AI} />
    }


    const forceUpdate = useForceUpdate()

    const [messages, setMessages] = useState<AIChatMessage[]>([])
    const [thinking, setThinking] = useState(false)
    const [loading, setLoading] = useState(false)
    const [tokens, setTokens] = useState<AiChatMessage_TokenUsage | null>(null)

    const scrollRef: React.RefObject<any> = useRef()
    const sendMessageArea: React.RefObject<any> = useRef()
    const textArea: React.RefObject<any> = useRef()

    const module = useMemo(()=>{
        const chatModule = new AiChatModule()
        chatModule.currentAIChat   = null
        chatModule.chatContext     = new AIChatContext({
            type        : props.aiOptions?.taskId ? AIChatContextType.TASK : AIChatContextType.ALL_SPACES,
            taskId      : props.aiOptions?.taskId,
            selectedText: props.selectedText
        })
        return chatModule
    }, [props.aiOptions?.taskId, props.selectedText])

    useLayoutEffect(() => {
        return gd.bus.subscribeNoContext(EVENT_BUS_LAYOUT_ON_HEIGHT_MAIN_CHANGE, reRender)
    }, [])

    const form: FormManager = useMemo(() => {
        return aiChat_initChatMessageForm()
    }, [])

    form.onSubmit = async (result: any) => {
        debug && console.log(debug, "onSubmit", result)

        // If (no Context when creating new chat) stop submitting
        if (!module.canSendMessage) {
            form.isSubmitting = false
            return
        }

        const formResult: any = _.cloneDeep(result)

        form.resetValues({
            message: '',
            messageRTF: null,
        })

        textArea.current.clear()

        setThinking(true)

        let response: any
        const chatId = module.currentAIChat?.id ?? undefined

        const newMessage = AIChatMessage.initUserQuery(
            formResult.message,
            formResult.messageJson,
            chatId
        )

        setMessages([...messages, newMessage])

        try {
            if (!module.currentAIChat) {
                // create chat
                response = await createNewAIChat(
                    formResult.message,
                    module.provider,
                    module.chatContext,
                    AiChatType.WRITE_WITH_AI,
                    module.agentId
                )

                module.currentAIChat = new AIChat(response)

            } else {
                // follow up
                response = await sendAIChatMessage(module.currentAIChat.id, formResult.message)
            }

            const chatMessages = AIChatMessage_Collection.initFromJSON(response?.messages)

            chatMessages.sortByMomentCreated()

            setMessages(chatMessages.models)
            setTokens(chatMessages.summaryTokens)

        } catch (e) {
            console.error(e)
            form.resetSubmit()
        }

        setThinking(false)

        form.reset()

        scrollRef.current?.scrollToBottom(0)

        // reRender()
    }

    const onSelectPrompt = (promptMessage: string) => {
        debug && console.log(debug, 'onSelectPrompt', promptMessage)

        if (
            !module.currentAIChat
            && !module.chatContext?.taskId
            && !module.chatContext?.projectId
        ) {
            console.log(debug, "module.currentAIChat =", module.currentAIChat,
                "module.newChatContext?.taskId =", module.chatContext?.taskId,
                "module.newChatContext?.projectId =", module.chatContext?.projectId
            )
            return
        }

        form.setValue("message", promptMessage)
        form.setValue("messageRTF", null)
        form.onSubmitClick()
        scrollRef.current?.scrollToBottom(0)
    }

    const onClickBack = () => {
        debug && console.log(debug, "onClickBack")

        module.currentAIChat = null
        setMessages([])
        setThinking(false)
        setLoading(true)
        setTokens(null)
    }

    const reRender = () => {
        debug && console.log(debug, 'reRender')

        forceUpdate()
    }

    debug && console.log(debug, "render")

    const showMessages = messages.length > 0

    const classes = Classes("ai-chat m-l-20 m-r-20", props.className)

    return (
        <div className={classes}>

            <div className="content m-b-10">
                <div className="flex-row-left-center m-t-20 m-b-20">
                    <div className="ui5-title-small">Write with AI</div>
                </div>

                {!showMessages &&
                    <div className="ui5-txt-less-important fs-14">
                        How can I assist with your writing?
                        I can refine your text, correct grammar, and more.
                    </div>
                }

                {!showMessages &&
                    <AIChat_SuggestedActions
                        className           = 'm-t-15 m-b-30'
                        suggestedActions    = {props.aiOptions?.actions}
                        onSelect            = {onSelectPrompt}
                    />
                }

                {showMessages &&
                    <VScroll
                        ref             = {scrollRef}
                        className       = "flex-1"
                        containerClass  = "p-b-40"
                    >

                        <WriteWithAiChat_Messages
                            module      = {module}
                            thinking    = {thinking}
                            messages    = {messages}
                        />

                    </VScroll>
                }
            </div>


            <div className="input">
                <WriteWithAi_InputMessage
                    form            = {form}
                    scrollRef       = {scrollRef}
                    textArea        = {textArea}
                    sendMessageArea = {sendMessageArea}
                    module          = {module}
                />

                <WriteWithAi_ActionButtons
                    className   = "m-t-10 m-b-10"
                    module      = {module}
                    editor      = {props.editor}
                    onBack      = {onClickBack}
                />
            </div>

        </div>
    )
}
